import EventEmitter from 'events';
import _ from 'lodash';
import Permissions from './Permissions';
import Devices from './Devices';
import { permissionAPI, frameRateTrials, injectDeviceConstraints, duplicateObject, isChrome } from './lib';
import LocalMediaStream from './LocalMediaStream';
import LoggerWrapper from '../../modules/logger-wrapper';

import CodecTester from '../RecordMediaLib/CodecTester.js';
import SafeMediaStream from './SafeMediaStream';

const tester = new CodecTester();

const logger = new LoggerWrapper('MEDIA_CORE', 'MediaCore', LoggerWrapper.DEBUG);
logger.setRemoteVerbosity(LoggerWrapper.DEBUG);

const deviceLocalStorageKey = 'devices.riverside.fm';
const REQUEST_TRIES = 10; // Number of request that app can do before triggering an error. (includes normal requests and recovers)
const REQUEST_LIMIT_TIMEOUT = 5000; // in ms, time when the request try counter will be reset.
class MediaCore extends EventEmitter {
	constructor(constraints, featureFlags) {
		super(arguments);

		this.featureFlags = featureFlags;
		this.metaData = {};
		this.mediaStream = null;
		this.liveCallMediaStream = null;
		this.firstRequest = true;
		this.initialized = false;
		this.requestMediaStreamReady = false;
		this.pendingMediaStreamStop = false;
		this._permissions = new Permissions();
		this._devices = new Devices();
		this.constraints = constraints || {};
		this.baseConstraints = constraints || {};
		this._frameRateTrials = [...frameRateTrials];
		this._frameRateRequirement = isChrome && !this.featureFlags.frameRateVariation ? 'exact' : 'min';
		this._recoverCounter = REQUEST_TRIES;
		this._verifyConstraintsInterval = null;
		this._recoverTimer = null;
		this._deviceReleaseQueue = [];
		this.silentStream = null;

		this.activeDevices = {
			audioinput: null,
			videoinput: null,
			audiooutput: null,
		};

		// different to active devices, because we might select one that can't be selected
		this.selectedDevices = {
			audioinput: null,
			videoinput: null,
			audiooutput: null,
		};

		this._addListeners();
	}

	setMetadata(metaData) {
		this.metaData = metaData;
	}

	async initialize(request = true) {
		await this._permissions.promise; // Wait for permissions initialization.
		const hasSomePermissions = await this._permissions.hasSomePermissions();

		if (request && hasSomePermissions) {
			await this.requestUserMedia();
		} else if (!request) {
			this.constraints = { audio: false, video: false };
		}

		this.initialized = true;
	}

	_addListeners() {
		logger.debug('Initializing MediaCore listeners...');
		this._permissions.on('permissions:changed', this._handlePermissionsChange.bind(this));
		this._devices.on('devices:discconected', this._handleDeviceDisconnection.bind(this));
		this._devices.on('devices:connected', this._handleDeviceConnection.bind(this));
	}

	async _handlePermissionsChange(permissions, prevPermissions) {
		logger.info(
			`User changed permissions.`,
			{ permissions, prevPermissions },
			{ functionName: '_handlePermissionsChange' }
		);

		this._devices.updateDevices();

		if (this.mediaStream) {
			logger.info(
				`Media stream exists while permissions change - calling _recoverUserMedia.`,
				{},
				{ functionName: '_handlePermissionsChange' }
			);
			this._recoverCounter = REQUEST_TRIES;
			this._recoverUserMedia();
		}
	}

	_handleDeviceDisconnection(devices) {
		const deviceIds = devices.map((d) => d.deviceId);
		const newConstraints = duplicateObject(this.constraints);
		let lostActiveDevice = false;
		if (deviceIds.includes(this.activeDevices?.audioinput?.deviceId)) {
			const device = duplicateObject(this.activeDevices.audioinput);
			logger.warn(
				`User lost connection with active device [${device.label}].`,
				{ participant: this.metaData.currentParticipant },
				{ functionName: '_handleDeviceDisconnection' }
			);

			if (this.constraints.audio !== false) {
				newConstraints.audio.deviceId = undefined;
			}

			this.activeDevices.audioinput = null;
			lostActiveDevice = true;
		}

		if (deviceIds.includes(this.activeDevices?.videoinput?.deviceId)) {
			const device = duplicateObject(this.activeDevices.videoinput);
			logger.warn(
				`User lost connection with active device [${device.label}].`,
				{ participant: this.metaData.currentParticipant },
				{ functionName: '_handleDeviceDisconnection' }
			);

			if (this.constraints.video !== false) {
				newConstraints.video.deviceId = undefined;
			}

			this.activeDevices.videoinput = null;
			lostActiveDevice = true;
		}

		if (lostActiveDevice) {
			this.emit('device:lost:active');
		}

		this.emit('device:disconnected', devices);

		this.constraints = newConstraints;
		this._updateActiveDevices();
	}

	async _handleDeviceConnection(devices) {
		const permissions = await this._permissions.getPermissions();
		if (devices.some((d) => d.kind === 'videoinput') && !permissions.camera) {
			await this._permissions.requestPermissions({ audio: false, video: true }, this.constraints);
		}

		if (devices.some((d) => d.kind === 'audioinput') && !permissions.microphone) {
			await this._permissions.requestPermissions({ audio: true, video: false }, this.constraints);
		}

		this.emit('device:connected', devices);
	}

	_handleIncomingRawAudioData(e) {
		this.emit('audio:raw', e);
	}
	_handleTrackMute(type, muted) {
		this.emit('stream:track:mute', type, muted);
	}

	_handleAudioSuspended() {
		this.emit('audio:suspended');
	}

	async _handleResolutionAutoAdapter() {
		this._updateActiveDevices();
	}

	async _handleTrackLost(type, reason, track, e) {
		logger.debug('Track lost.', { type, track });

		if (!permissionAPI) {
			// Fallback to detect permission change and trying to recover.
			const perm = await this._permissions.hasAllPermissions();

			if (!perm) {
				logger.debug('Fallback track detection lost device permission');
				this._recoverUserMedia();
			}
		}

		// Disconnection event is triggered after the track is lost.
		// so, if we try to detect if the device is still connected right away, it will always be true.
		await this._devices.updateDevices();
		const connectedDeviceIds = this._devices.getDevices().map((d) => d.deviceId);
		const trackDeviceId = track?.deviceId;

		if (trackDeviceId !== 'default' && connectedDeviceIds.includes(trackDeviceId)) {
			logger.debug(`Device track lost but still connected, trying to recover media. [${type}]`);
			this._recoverUserMedia();
		} else {
			logger.debug(`Device disconnected. Recovering with different device. [${type}]`);
			this._recoverUserMedia();
		}

		// Stop live call track if lost.
		// TODO: Verify this is needed.
		if (this.liveCallMediaStream) {
			if (type === 'video') {
				this.liveCallMediaStream.videoTrack?.stop();
			} else {
				this.liveCallMediaStream.audioTrack?.stop();
			}
		}

		logger.warn(`User had a track stopped event.`, { reason, type }, { functionName: '_handleTrackLost' });
		this.emit('stream:track:lost', type, reason, e);
	}

	_saveSelectedDevices() {
		const serializedSelectedDevices = {
			audioinput: this.selectedDevices.audioinput?.deviceId,
			audiooutput: this.selectedDevices.audiooutput?.deviceId,
			videoinput: this.selectedDevices.videoinput?.deviceId,
		};
		localStorage.setItem(deviceLocalStorageKey, JSON.stringify(serializedSelectedDevices));
	}

	selectDevice(device, kind) {
		let deviceObj;

		if (device === 'mic_off' || device === 'cam_off') {
			deviceObj = null;
		} else {
			deviceObj = this._devices.getDevice(device, kind);
		}

		this.selectedDevices[kind] = deviceObj;
		logger.debug(`Selected [${kind}] device set to [${deviceObj?.label || null}]`);

		this._saveSelectedDevices();

		if (kind === 'audioinput' || kind === 'videoinput') {
			const type = kind === 'audioinput' ? 'audio' : 'video';
			if (deviceObj) {
				// we check if previous constraints was just a boolean, in that case we grab the baseConstraints
				let nextConstraints = duplicateObject(this.constraints);

				if (typeof nextConstraints[type] === 'boolean') {
					nextConstraints[type] = duplicateObject(this.baseConstraints[type]);
				}

				nextConstraints = this.modifyDeviceBasedConstraints(
					injectDeviceConstraints(nextConstraints, deviceObj.deviceId, kind)
				);

				if (this.mediaStream) {
					this.updateUserMedia(nextConstraints, false, `user changed ${kind} device`);
				} else {
					this.requestUserMedia(nextConstraints, false, `user changed ${kind} device`);
				}
			} else {
				const nextConstraints = duplicateObject(this.constraints);
				nextConstraints[type] = false;
				this.updateUserMedia(nextConstraints, false, `user changed ${kind} device to off`);
			}
		} else if (kind === 'audiooutput' && deviceObj) {
			this.setActiveDevice(deviceObj, kind);
		}

		return deviceObj;
	}

	_popFrameRateTrial(val) {
		this._frameRateTrials = this._frameRateTrials.filter((v) => v !== val);
		return this._frameRateTrials;
	}

	getDefaultDevicesConstraints() {
		const savedDevices = JSON.parse(localStorage.getItem(deviceLocalStorageKey)) || {};
		let nextConstraints = this.constraints;

		if (!!savedDevices.audioinput && this._devices.getDevice(savedDevices.audioinput)) {
			nextConstraints = injectDeviceConstraints(nextConstraints, savedDevices.audioinput, 'audioinput');
		} else {
			savedDevices.audioinput = null;
		}

		if (!!savedDevices.videoinput && this._devices.getDevice(savedDevices.videoinput)) {
			nextConstraints = injectDeviceConstraints(nextConstraints, savedDevices.videoinput, 'videoinput');
		} else {
			savedDevices.videoinput = null;
		}

		if (!!savedDevices.audiooutput) {
			this.selectDevice(savedDevices.audiooutput, 'audiooutput');
		} else {
			savedDevices.audiooutput = null;
		}

		localStorage.setItem(deviceLocalStorageKey, JSON.stringify(savedDevices));
		return nextConstraints;
	}

	_trackRequestTry() {
		if (this._recoverTimer) {
			clearTimeout(this._recoverTimer);
			this._recoverTimer = null;
		}

		this._recoverCounter--;
		logger.debug(`Numbers of requests left [${this._recoverCounter}]`);

		this._recoverTimer = setTimeout(() => {
			this._recoverCounter = REQUEST_TRIES;
			logger.debug('Reseting the number of request limit after timeout.');
		}, REQUEST_LIMIT_TIMEOUT);
	}

	/**
	 * This method will modify the constraints based on the selected device capabilities.
	 * @param {*} constraints
	 * @returns
	 */
	modifyDeviceBasedConstraints(constraints) {
		const nextConstraints = duplicateObject(constraints);

		if (typeof nextConstraints?.video === 'object' && nextConstraints?.video?.deviceId?.exact) {
			const deviceId = nextConstraints?.video?.deviceId?.exact;
			const device = this._devices.getDevice(deviceId);
			const deviceConstraints = nextConstraints.video;

			if (deviceConstraints?.frameRate) {
				const requiredFps = this.baseConstraints?.video?.frameRate?.[this._frameRateRequirement];
				const maxFps = device.capabilities.frameRate.max;
				const setupFps = requiredFps > maxFps ? maxFps : requiredFps;
				logger.debug(`Max camera FPS based on device and settings is [${setupFps}]`);
				deviceConstraints.frameRate = { [this._frameRateRequirement]: setupFps };
			}

			nextConstraints.video = deviceConstraints;
		}

		return nextConstraints;
	}

	_verifyResolution() {
		if (this.mediaStream.hasVideo) {
			logger.debug('Verify resolution');
			const activeDevice = this.activeDevices.videoinput;
			const streamResolution = this.mediaStream.videoTrack.getSettings()?.height;
			const maxResolution = activeDevice.capabilities.height.max;
			const requiredResolution = Math.min(this.constraints?.video?.height?.ideal, maxResolution);

			if (streamResolution <= 480 && streamResolution < requiredResolution) {
				logger.warn(
					`Error getting the right resolution from device. [Device Max: ${maxResolution}p | Required ${requiredResolution}p | Got: ${streamResolution}p]`
				);
				this._handleMediaError({
					name: 'WrongResolutionError',
					requested: requiredResolution,
					delivered: streamResolution,
					deviceMax: maxResolution,
				});
			}
		}
	}
	_setVideoTrackContentHints() {
		const track = this.mediaStream?.videoTrack;
		if (!track) return;
		if ('contentHint' in track) {
			track.contentHint = 'motion';
			if (track.contentHint !== 'motion') {
				console.log('Invalid video track contentHint: ');
			} else {
				console.log('MediaStream track content hint set');
			}
		} else {
			console.log('MediaStreamTrack contentHint attribute not supported');
		}
	}

	_verifyConstraints() {
		logger.debug('Verify constraints.');
		if (this.mediaStream) {
			const diff = [];
			if (this.mediaStream.hasVideo) {
				const trackSettings = this.mediaStream.videoTrack.getSettings();
				const constraints = this.baseConstraints.video;

				const challenges = {
					height: {
						result: trackSettings.height,
						required: constraints.height.ideal,
					},
					width: {
						result: trackSettings.width,
						required: constraints.width.ideal,
					},
					frameRate: {
						result: trackSettings.frameRate,
						required: constraints.frameRate?.[this._frameRateRequirement],
						compare: 'gte',
					},
				};

				Object.keys(challenges).forEach((key) => {
					if (!challenges[key].compare) {
						if (challenges[key].required !== challenges[key].result) {
							diff.push({
								type: 'video',
								key,
								details: challenges[key],
							});
						}
					} else if (challenges[key].compare === 'gte') {
						if (challenges[key].required > challenges[key].result) {
							diff.push({
								type: 'video',
								key,
								details: challenges[key],
							});
						}
					}
				});
			}

			if (this.mediaStream.hasAudio) {
				const trackSettings = this.mediaStream.audioTrack.getSettings();
				const constraints = this.baseConstraints.audio;

				logger.debug('Audio Track Settings', { trackSettings });

				const challenges = {
					channelCount: {
						result: trackSettings.channelCount,
						required: constraints.channelCount.ideal,
					},
					noiseSuppression: {
						result: trackSettings.noiseSuppression,
						required: constraints.noiseSuppression.ideal,
					},
					sampleSize: {
						result: trackSettings.sampleSize,
						required: constraints.sampleSize.ideal,
					},
					sampleRate: {
						result: trackSettings.sampleRate,
						required: constraints.sampleRate.ideal,
					},
					autoGainControl: {
						result: trackSettings.autoGainControl,
						required: constraints.autoGainControl.ideal,
					},
					echoCancellation: {
						result: trackSettings.echoCancellation,
						required: constraints.echoCancellation.ideal,
					},
				};

				Object.keys(challenges).forEach((key) => {
					if (challenges[key].required !== challenges[key].result) {
						diff.push({
							type: 'audio',
							key,
							details: challenges[key],
						});
					}
				});
			}

			if (diff.length > 0) {
				logger.warn(`There are ${diff.length} difference(s) between requested stream and delivered.`, {
					diff,
					constraints: JSON.stringify(this.constraints),
				});
				clearInterval(this._verifyConstraintsInterval); // Stop checking. It's already wrong.
			}
		}
	}

	_verifyMediaStream() {
		try {
			this._verifyResolution();
			if (this.featureFlags?.constraintsChecker) {
				this._verifyConstraints();
				this._verifyConstraintsInterval = setInterval(() => {
					this._verifyConstraints();
				}, 60 * 1000); // check every minute
			}
		} catch (err) {
			logger.error('There was an error on mediaStream verification.', null, { err });
		}
	}

	async requestUserMedia(constraints, force = false, reason = 'default') {
		if (!this.mediaStream || force) {
			this._trackRequestTry();

			this.constraints = Object.assign({}, duplicateObject(this.constraints), duplicateObject(constraints));
			this._popFrameRateTrial(this.constraints?.video?.frameRate?.[this._frameRateRequirement]);
			try {
				this.emit('stream:generating', constraints);
				const hasAllPermissions = await this._permissions.hasAllPermissions();

				if (!hasAllPermissions) {
					await this._permissions.requestPermissions(
						{ audio: !!this.constraints.audio, video: !!this.constraints.video },
						this.constraints
					);
				}

				if (this.firstRequest) {
					await this._devices.promise;
					this.constraints = this.getDefaultDevicesConstraints();
					this.constraints = this.modifyDeviceBasedConstraints(this.constraints);
					this.firstRequest = false;
				}

				logger.debug('Requesting user media with constraints', {
					constraints: duplicateObject(this.constraints),
					reason,
				});

				const result = await navigator.mediaDevices.getUserMedia(this.constraints);

				logger.info(
					`User made a user media request [${reason}].`,
					{
						reason,
						constraints: this.constraints,
						result,
					},
					{ functionName: 'requestUserMedia' }
				);

				// stop current media stream before initializing a new one
				if (this.mediaStream) {
					logger.info(
						'Stopping current media stream before initializing a new one',
						{},
						{ functionName: 'requestUserMedia' }
					);
					await this.stopMediaStream();
				}

				if (this.featureFlags.safeAudioTrack) {
					logger.debug('Initializing SafeMediaStream as core media stream...');
					this.mediaStream = new SafeMediaStream(result);
				} else {
					logger.debug('Initializing LocalMediaStream as core media stream...');
					this.mediaStream = new LocalMediaStream(result);
				}

				// Waits for mediaStream building process to be finished.
				await this.mediaStream.building;

				if (this.mediaStream.videoTrack) {
					tester.initDevice(this.mediaStream.videoTrack);
				}

				try {
					// create a silent stream instance related to the new user media
					if (this.mediaStream.audioTrack) {
						const silentAudioTrack = this.mediaStream.audioTrack.clone();
						this.silentStream = silentAudioTrack && new LocalMediaStream(new MediaStream([silentAudioTrack]));
					}
				} catch (err) {
					logger.warn('Failed to create silent stream', null, { err });
				}

				const logData = {};

				if (this.mediaStream.hasVideo) {
					logData.video = {
						muted: this.mediaStream.isVideoMuted,
						device: this._devices.getDeviceMetadata(this.mediaStream.getVideoDeviceId(), 'videoinput'),
						activeConstraints: this.mediaStream.getVideoDeviceDetails(),
					};
				}

				if (this.mediaStream.hasAudio) {
					logData.audio = {
						muted: this.mediaStream.isAudioMuted,
						device: this._devices.getDeviceMetadata(this.mediaStream.getAudioDeviceId(), 'audioinput'),
						activeConstraints: this.mediaStream.getAudioDeviceDetails(),
					};
				}
				logger.debug('Resulting MediaStream', { mediaStream: this.mediaStream });
				logger.info(`User got a media stream.`, { ...logData }, { functionName: 'requestUserMedia' });

				this._updateActiveDevices();
				this._updateActiveConstraints();

				this._verifyMediaStream();
				if (this.featureFlags?.vp9Recording) {
					this._setVideoTrackContentHints();
				}

				if (this.featureFlags?.singleStream) {
					this.emit('live:media:set', this.mediaStream);
				}
				this.emit('stream:set', this.mediaStream);
				this.mediaStream.on('track:lost', this._handleTrackLost.bind(this));
				this.mediaStream.on('track:mute:changed', this._handleTrackMute.bind(this));
				this.mediaStream.on('audio:suspended', this._handleAudioSuspended.bind(this));
				this.mediaStream.on('audio:raw', this._handleIncomingRawAudioData.bind(this));

				this.mediaStream.on('track:adapter:resolution', this._handleResolutionAutoAdapter.bind(this));

				this._frameRateTrials = [...frameRateTrials]; // reset trials
				this.requestMediaStreamReady = true;
				if (this.pendingMediaStreamStop) {
					await this.stopMediaStream();
				}
				return result;
			} catch (err) {
				logger.debug('Error getUserMedia', null, { err });

				let shouldRetry = this._recoverCounter > 0;
				if (!shouldRetry) {
					logger.debug('Maximum number of retry treshold.');
				}
				if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
					logger.error(
						`User encountered a getUserMedia (media request) error!`,
						{
							constraints: this.constraints,
							errorMessage: err?.message || err?.name,
							reason,
						},
						{ functionName: 'requestUserMedia', err }
					);

					this._handleMediaError(err);
				} else if (err.name === 'NotReadableError') {
					const audioError = err?.message?.includes('audio');
					const videoError = err?.message?.includes('video');
					let extraConstraints = {};

					this._handleMediaError(err);

					if (videoError) {
						this.setActiveDevice(null, 'videoinput');
						extraConstraints.video = false;
					}
					if (audioError) {
						this.setActiveDevice(null, 'audioinput');
						extraConstraints.audio = false;
					}

					if ((!audioError || !videoError) && shouldRetry) {
						return this.requestUserMedia(
							extraConstraints,
							true,
							`Retry because of NotReadableError error [${err.constraint}].`
						);
					}

					logger.error(
						`Media device not readable. [audio error: ${audioError}] [video error: ${videoError}]`,
						{
							constraints: this.constraints,
							reason,
						},
						{ functionName: 'requestUserMedia', err }
					);
				} else if (err.name === 'OverconstrainedError' && shouldRetry) {
					let errorConstraint = err.constraint; // just for messaging/logging

					logger.error(
						`Overconstrained because of [${errorConstraint}]`,
						{
							constraints: this.constraints,
							reason,
						},
						{ functionName: 'requestUserMedia', err }
					);

					if (err.constraint === 'frameRate') {
						const fps = this._frameRateTrials[0] || undefined;
						logger.debug(`Trying with different frameRate [${fps}]...`);
						this.constraints.video.frameRate = { [this._frameRateRequirement]: fps };
					} else if (err.constraint === 'deviceId') {
						let modifiedConstraints = duplicateObject(this.constraints);
						await this._devices.updateDevices();
						const audioDevice = this._devices.getDevice(this.constraints.audio?.deviceId?.exact);
						const videoDevice = this._devices.getDevice(this.constraints.video?.deviceId?.exact);

						if (!videoDevice) {
							logger.debug(`Video device overconstrained, removing constraint`);
							delete modifiedConstraints.video.deviceId;
						}

						if (!audioDevice) {
							logger.debug(`Audio device overconstrained, removing constraint`);
							delete modifiedConstraints.audio.deviceId;
						}

						if (audioDevice && videoDevice) {
							// This shouldn't happen, but Chrome have a weird issue where sometimes triggers this deviceId Overconstrained when it's not.
							// And it happens when frameRate is set to exact. But we need that.
							modifiedConstraints.video.frameRate = { [this._frameRateRequirement]: undefined };
							errorConstraint = 'fake-deviceId'; // Just to track this issue on logger.
							this._handleMediaError({ name: 'GenericError' });
							logger.warn(`Removing frameRate constraint because of Chrome being locked...`);
						}

						return this.requestUserMedia(
							modifiedConstraints,
							true,
							`Retry because device OverconstrainedError error [${errorConstraint}].`
						);
					} else {
						// Overconstrain is not handled.
						logger.error(
							`Overconstrained and app wasn't able to retry. [${err.constraint}]`,
							{
								constraints: duplicateObject(this.constraints),
								reason,
							},
							{ functionName: 'requestUserMedia', err }
						);
						this._handleMediaError({ name: 'genericUserMediaError' });
						return { error: true, details: err, constraints: this.constraints };
					}

					return this.requestUserMedia({}, true, `Retry because of OverconstrainedError error [${err.constraint}].`);
				} else if (err.name === 'NotFoundError' && shouldRetry) {
					// Adding constraints to error.
					err.constraints = duplicateObject(this.constraints);

					if (this._devices.getDevices('videoinput').length === 0) {
						logger.debug('No camera found.');
						this.constraints.video = false;
					}

					if (this._devices.getDevices('audioinput').length === 0) {
						logger.debug('No audio found.');
						this.constraints.audio = false;
					}

					return this.requestUserMedia(
						{},
						true,
						`Retry with updated constraints. Audio Device: ${!!this.constraints.audio} Video device: ${!!this
							.constraints.video}`
					);
				} else {
					this._handleMediaError(err);
					logger.error('Unhandled media error', null, { err });
				}

				return { error: true, details: err, constraints: this.constraints };
			}
		} else {
			return this.updateUserMedia(constraints);
		}
	}

	getSilentStream = () => this.silentStream?.mediaStream;
	getLiveStream = () => {
		return this.liveCallMediaStream ? this.liveCallMediaStream?.mediaStream : this.mediaStream?.mediaStream;
	};

	async getLiveCallMediaStream(settings) {
		try {
			const constraints = { audio: false, video: false };

			if (this.mediaStream?.hasAudio) {
				constraints.audio = {
					autoGainControl: false,
					echoCancellation: true,
					noiseSuppression: settings.noiseSuppression,
					deviceId: { exact: this.mediaStream.getAudioDeviceId() },
				};
			}

			if (this.mediaStream?.hasVideo) {
				constraints.video = {
					width: { ideal: settings.resolution.width },
					height: { ideal: settings.resolution.height },
					deviceId: { exact: this.mediaStream.getVideoDeviceId() },
				};
			}

			if (constraints.audio || constraints.video) {
				logger.debug('Getting Live Call User Media');
				const result = await navigator.mediaDevices.getUserMedia(constraints);
				this.liveCallMediaStream = new LocalMediaStream(result);

				const logData = {};

				if (this.liveCallMediaStream.hasVideo) {
					logData.video = {
						muted: this.liveCallMediaStream.isVideoMuted,
						device: this._devices.getDeviceMetadata(this.liveCallMediaStream.getVideoDeviceId(), 'videoinput'),
						activeConstraints: this.liveCallMediaStream.getVideoDeviceDetails(),
					};
				}

				if (this.liveCallMediaStream.hasAudio) {
					logData.audio = {
						muted: this.liveCallMediaStream.isAudioMuted,
						device: this._devices.getDeviceMetadata(this.liveCallMediaStream.getAudioDeviceId(), 'audioinput'),
						activeConstraints: this.liveCallMediaStream.getAudioDeviceDetails(),
					};
				}

				logger.info(`Got Live Call User Media.`, { ...logData }, { functionName: 'getLiveCallMediaStream' });
				this.emit('live:media:set');
				return this.liveCallMediaStream;
			} else {
				logger.debug('No active contrainsts selected.');
			}
		} catch (err) {
			logger.error('Error getting Live Call media', null, { err });
		}
	}

	/**
	 * Forces non default device id when selected audio device is default
	 * by searching for the duplicate, non default listing and returning it's id
	 */
	_getAudioDeviceId() {
		// get the device id and return immediately if not default
		const deviceId = this.mediaStream.getAudioDeviceId();
		if (deviceId !== 'default') {
			return deviceId;
		}
		// get the default device's group id, retrieve and return it's actual (non default) id
		// note: falls back to media stream's audio device id
		const { groupId } = this.mediaStream.getAudioDeviceDetails();
		const nonDefaultDeviceId = this._devices.getDeviceByDetails({ groupId, kind: 'audioinput' });
		return nonDefaultDeviceId ? nonDefaultDeviceId : deviceId;
	}

	_handleMediaError(error) {
		const toDispatch = { type: 'setError' };

		if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
			toDispatch.payload = { type: 'permissionDenied', constraints: error.constraints || this.constraints };
		} else if (
			error.name === 'NotReadableError' ||
			error.name === 'TrackStartError' ||
			error.name === 'NotFoundError' ||
			error.message === 'Could not start video source'
		) {
			toDispatch.payload = { type: 'trackStartError', constraints: error.constraints || this.constraints };
		} else if (error.name === 'GenericError') {
			toDispatch.payload = { type: 'genericError' };
		} else if (error.name === 'WrongResolutionError') {
			toDispatch.payload = { ...error, type: 'wrongResolutionError' };
		} else {
			toDispatch.payload = { type: 'genericUserMediaError', constraints: error.constraints || this.constraints };
		}

		this.emit('media:error', toDispatch);
	}

	async stopMediaStream() {
		logger.debug('Stop media stream requested.', {
			mediaStream: this.mediaStream,
			requestMediaStreamReady: this.requestMediaStreamReady,
		});

		if (!this.requestMediaStreamReady) {
			this.pendingMediaStreamStop = true;
		} else {
			if (this.mediaStream) {
				this.mediaStream.removeAllListeners();
				this.mediaStream.stop();
			}

			if (this.liveCallMediaStream) {
				this.liveCallMediaStream.removeAllListeners();
				this.liveCallMediaStream.stop();
			}

			if (this.silentStream) {
				this.silentStream.removeAllListeners();
				this.silentStream.stop();
			}

			this.mediaStream = null;
			this.liveCallMediaStream = null;
			this.silentStream = null;

			if (this._verifyConstraintsInterval) {
				clearInterval(this._verifyConstraintsInterval);
				this._verifyConstraintsInterval = null;
			}

			this.emit('stream:set', null);
		}
	}

	async _recoverUserMedia() {
		let result = null;

		logger.debug(`Trying to recover user media, ${this._recoverCounter} tries left`);

		if (this._recoverCounter > 0) {
			this.stopMediaStream(); // Stop previous;
			result = await this.requestUserMedia(this.constraints, true, 'recovering from track lost');
		} else {
			logger.warn('Maximum recover tries reached. Displaying error.');
			// if retry limit is reached, we emit error event.
			result = { error: true, message: 'Maximum retries reached' };
			this._handleMediaError({ name: 'TrackStartError' });
		}

		return result;
	}

	_shouldForceUpdate(constraints) {
		let result = false;

		if (constraints.video && !this.mediaStream.hasVideo) {
			result = true;
		}

		if (constraints.audio && !this.mediaStream.hasAudio) {
			result = true;
		}

		return result;
	}

	waitForRelease() {
		let wait = null;

		// Timeout promise for not hanging forever in case of issues.
		const timeoutPromise = new Promise((resolve, reject) => {
			wait = setTimeout(() => {
				logger.debug('Release recorder timeout.');
				clearTimeout(wait);
				resolve();
			}, 8000);
		});

		const releasePromise = Promise.all(this._deviceReleaseQueue).then(() => {
			this._deviceReleaseQueue = [];
			clearTimeout(wait);
		});

		return Promise.race([releasePromise, timeoutPromise]);
	}

	addReleaseTask(promise) {
		if (promise) {
			this._deviceReleaseQueue.push(promise);
		}
	}

	async updateUserMedia(constraints, updatesBase = false, reason) {
		const prevConstraints = duplicateObject(this.constraints);
		const nextConstraints = _.merge({}, prevConstraints, constraints);
		let result = null;

		logger.debug('Updating media stream', { reason, prevConstraints, nextConstraints });

		if (updatesBase) {
			this.baseConstraints = constraints;

			// We don't update constraints if the device was off
			// unless there is a device selected.
			if (!prevConstraints.audio && !nextConstraints.audio?.deviceId) {
				nextConstraints.audio = false;
			}

			if (!prevConstraints.video && !nextConstraints.video?.deviceId) {
				nextConstraints.video = false;
			}
		}

		if (
			this.mediaStream &&
			(!_.isEqual(prevConstraints, nextConstraints) || this._shouldForceUpdate(nextConstraints))
		) {
			this.emit('stream:releasing');
			await this.waitForRelease();

			await this.stopMediaStream();
			if (nextConstraints.audio || nextConstraints.video) {
				result = this.requestUserMedia(nextConstraints, false, reason);
			} else {
				this.constraints = nextConstraints;
				this._updateActiveDevices();
				this.emit('stream:set', null);
				if (this.featureFlags?.singleStream) {
					this.emit('live:media:set', this.mediaStream);
				}
			}
		}

		return result;
	}

	setActiveDevice(deviceDetails, kind) {
		let result = null;
		if (deviceDetails !== null) {
			const deviceObj = this._devices.getDevice(deviceDetails.deviceId, kind);
			this.activeDevices[kind] = { ...deviceDetails, ...deviceObj };
			logger.debug(`Active [${kind}] device set to [${deviceObj?.label}]`);
			this.selectedDevices[kind] = deviceObj; // making sure the selected device is up to date once is active.
		} else {
			this.activeDevices[kind] = null;
			this.selectedDevices[kind] = null;
		}

		result = this.activeDevices[kind];
		this.emit(`active:${kind}:changed`, result);
	}

	_updateActiveDevices() {
		if (this.constraints.audio && this.mediaStream) {
			this.setActiveDevice(this.mediaStream.getAudioDeviceDetails(), 'audioinput');
		} else {
			logger.debug('Audio track not present in mediaStream. Setting active device as null');
			this.setActiveDevice(null, 'audioinput');
		}

		if (this.constraints.video && this.mediaStream) {
			this.setActiveDevice(this.mediaStream.getVideoDeviceDetails(), 'videoinput');
		} else {
			logger.debug('Video track not present in mediaStream. Setting active device as null');
			this.setActiveDevice(null, 'videoinput');
		}

		const audioOutputDeviceId = this.activeDevices?.audiooutput?.deviceId;
		if (this.activeDevices.audiooutput && audioOutputDeviceId && this._devices.getDevice(audioOutputDeviceId)) {
			this.setActiveDevice(this.activeDevices.audiooutput, 'audiooutput');
		} else if (this._devices.getDevices('audiooutput').length > 0) {
			this.setActiveDevice(this._devices.getDevices('audiooutput')[0], 'audiooutput');
		}
	}

	_updateActiveConstraints() {
		if (this.constraints.audio) {
			this.constraints.audio.deviceId = this.activeDevices.audioinput
				? { exact: this.activeDevices.audioinput.deviceId }
				: undefined;
		}

		if (this.constraints.video) {
			this.constraints.video.deviceId = this.activeDevices.videoinput
				? { exact: this.activeDevices.videoinput.deviceId }
				: undefined;
		}
	}

	getPermissions() {
		return this._permissions.getPermissions();
	}

	getConstraints() {
		return this.constraints;
	}
}

export default MediaCore;
